import { useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import GenerateDocumentButton from '@/components/common/GenerateDocumentButton';
import LoadingPage from '@/components/common/LoadingPage';
import PlatformLink from '@/components/common/PlatformLink';
import PropertiesList from '@/components/common/PropertiesList';
import SubPageHeader from '@/components/common/SubPageHeader';
import { Button } from '@/components/shadcn/button';
import getInsurerPortalUrl from '@/features/crm/utils/anva/getInsurerPortalUrl';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import { toDateString } from '@/lib/utils/date';
import formatCurrency from '@/lib/utils/formatCurrency';
import queryKeys from '@/queries/queryKeys';

import useSelectedParty from '../analysis/hooks/useSelectedParty';

import usePolicy from './queries/hooks/usePolicy';
import { dueDateToReadable, formatPremiumWithInterval } from './utils';
import getMutationUrl from './utils/getMutationUrl';

export default function PoliciesDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();

  const queryClient = useQueryClient();

  const party = useSelectedParty();

  const { openSendMail } = useEmailClient();

  const { data: policy, isLoading } = usePolicy(id ?? '');

  const externalPortalUrl = getInsurerPortalUrl({
    insurerName: policy?.insurance_company,
    anvaProductRef: policy?.anva_product_ref,
  });

  const mutationUrl = getMutationUrl({
    partyId: party?.id,
    policyId: id,
    type: party?.type === 'CUSTOMER' ? 'CUSTOMER' : 'COMPANY',
  });

  const handleSendEmailInsurerPortalUrlMissing = () => {
    const subject = t('POLICY_PORTAL_EMAIL_SUBJECT', {
      insurer: policy?.insurance_company ?? t('PARTY_CHECKUP_UNKNOWN_INSURER'),
    });
    const body = t('POLICY_PORTAL_EMAIL_BODY', {
      insurer: policy?.insurance_company || t('PARTY_CHECKUP_UNKNOWN_INSURER'),
    });

    openSendMail({
      to: ['support@wegroup.nl'],
      subject,
      body,
    });
  };

  const handleInvalidatePolicy = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.policies.byId(id ?? ''),
    });

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <SubPageHeader>
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span>
              {t('POLICY_DETAIL_TITLE')} {policy?.name ?? ''}
            </span>

            {policy?.carrier_type && (
              <span className="bg-card rounded border px-1 py-0.5 text-xs text-nowrap">
                {t(`CARRIER_TYPE_${policy.carrier_type}`)}
              </span>
            )}
          </div>

          <Button size="icon-sm" variant="ghost" asChild>
            <PlatformLink
              to={`/parties/${party?.id}/current-insurances?search=${policy?.policy_nr}`}
              target="_blank"
            >
              <ExternalLink />
            </PlatformLink>
          </Button>
        </div>
      </SubPageHeader>

      <main className="flex-1 overflow-y-auto">
        {isLoading && <LoadingPage />}

        {!isLoading && policy && (
          <div className="mt-1 flex flex-col gap-3 p-2">
            <PropertiesList
              items={[
                {
                  label: t('CLAIMS_POLICY_NR'),
                  value: policy.policy_nr,
                  copy: true,
                },
                {
                  label: t('STATUS'),
                  value: t(`STATUS_${policy.status}`).toUpperCase(),
                },
                {
                  label: t('PREMIUM'),
                  value: formatPremiumWithInterval(
                    policy.premium,
                    policy.payment_interval,
                    t,
                  ),
                },
                {
                  label: t('POLICY_DEDUCTIBLE'),
                  value: formatCurrency(policy.deductible),
                },
                {
                  label: t('INSURER'),
                  value: policy.insurance_company,
                },
                {
                  label: t('START_DATE'),
                  value: toDateString(policy.start_date),
                },
                {
                  label: t('MAIN_DUE_DATE'),
                  value: dueDateToReadable(policy.due_date),
                },
                {
                  label: t('CREATED_AT'),
                  value: toDateString(policy.created_at),
                },
              ]}
            />

            <PropertiesList
              columns={1}
              items={[
                {
                  label: t('POLICY_DOCUMENT'),
                  value: policy.policy_document_link ? (
                    <GenerateDocumentButton
                      link={policy.policy_document_link}
                      onGenerate={handleInvalidatePolicy}
                      generateLabel={
                        <>
                          {t('GENERATE_DOCUMENT')}

                          <Download className="size-4" />
                        </>
                      }
                      className="flex items-center gap-1 hover:underline"
                      variant="outline"
                      size="sm"
                    >
                      {t('OPEN_DOCUMENT')}

                      <ExternalLink className="size-4" />
                    </GenerateDocumentButton>
                  ) : (
                    <div className="text-muted-foreground/50">
                      {t('DOCUMENT_UNAVAILABLE')}
                    </div>
                  ),
                },
                {
                  label: t('POLICY_PORTAL_TITLE'),
                  value: externalPortalUrl ? (
                    <Link
                      to={externalPortalUrl}
                      className="flex items-center gap-1 hover:underline"
                      target="_blank"
                    >
                      {t('POLICY_PORTAL_OPEN')}

                      <ExternalLink className="size-4" />
                    </Link>
                  ) : (
                    <div className="flex flex-col items-start text-xs leading-normal">
                      <span>{t('POLICY_PORTAL_UNKNOWN')}</span>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSendEmailInsurerPortalUrlMissing}
                      >
                        {t('POLICY_PORTAL_PROVIDE_LINK')}
                      </Button>
                    </div>
                  ),
                },
                {
                  label: t('POLICY_MUTATION_TITLE'),
                  value: (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      asChild
                    >
                      <Link to={mutationUrl} target="_blank">
                        {t('POLICY_MUTATION_OPEN')}

                        <ExternalLink className="size-4" />
                      </Link>
                    </Button>
                  ),
                },
              ]}
            />
          </div>
        )}
      </main>
    </div>
  );
}
